import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Calendar, Clock, Pencil } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import type { Json } from "@/integrations/supabase/types";
import { useState, useEffect, useMemo, useCallback } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { useTeamId } from "@/hooks/useTeamId";
import { useCompanyData } from "@/hooks/useCompanyData";
import { calculateScores } from "@/services/scoring/calculateScores";
import { toast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

const EXIT_TIMELINE_MAP: Record<string, string> = {
  within_1_year: "Under 1 year",
  "1_2_years": "1–2 years",
  "2_5_years": "2–5 years",
  "5_10_years": "5–10 years",
  over_10_years: "10+ years",
  not_sure: "Timeline not yet set",
};

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

const A2_ASSESSMENT_ID = "30d8b24c-8a95-45e5-867e-82bb9c335d1e";
const SUCCESSION_QUESTION_ID = "1c4a72c4-d7bf-42fb-819c-d550fabca3a9";

function getTimeRemaining(targetDate: Date): { label: string; isOverdue: boolean } {
  const now = new Date();
  const diffMs = targetDate.getTime() - now.getTime();

  if (diffMs < 0) {
    return { label: "Timeline overdue", isOverdue: true };
  }

  const diffYears = diffMs / (365.25 * 24 * 60 * 60 * 1000);

  if (diffYears < 1) {
    const months = Math.round(diffMs / (30.44 * 24 * 60 * 60 * 1000));
    return { label: `${months} month${months !== 1 ? "s" : ""} remaining`, isOverdue: false };
  }

  const rounded = Math.round(diffYears * 10) / 10;
  return { label: `${rounded} years remaining`, isOverdue: false };
}

/** Check if a given year+month is at least 6 months from today */
function isMonthDisabled(year: number, monthIndex: number): boolean {
  const now = new Date();
  const target = new Date(year, monthIndex, 1);
  const minDate = new Date(now.getFullYear(), now.getMonth() + 6, 1);
  return target < minDate;
}

function isYearFullyDisabled(year: number): boolean {
  // A year is fully disabled if even December of that year is before minDate
  return isMonthDisabled(year, 11);
}

// ─── Year/Month Picker Modal ────────────────────────────────────────

interface YearMonthPickerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedYear: number | null;
  selectedMonth: number | null;
  onConfirm: (year: number, month: number) => void;
  saving: boolean;
}

function YearMonthPicker({
  open,
  onOpenChange,
  selectedYear: initialYear,
  selectedMonth: initialMonth,
  onConfirm,
  saving,
}: YearMonthPickerProps) {
  const [year, setYear] = useState<number | null>(initialYear);
  const [month, setMonth] = useState<number | null>(initialMonth);

  // Sync when modal opens with new initial values
  useEffect(() => {
    if (open) {
      setYear(initialYear);
      setMonth(initialMonth);
    }
  }, [open, initialYear, initialMonth]);

  const currentYear = new Date().getFullYear();
  const years = useMemo(() => {
    const arr: number[] = [];
    for (let y = currentYear + 1; y <= currentYear + 30; y++) {
      arr.push(y);
    }
    return arr;
  }, [currentYear]);

  const canConfirm = year !== null && month !== null && !saving;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold text-foreground">
            Select Your Succession Date
          </DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            Choose the year and month you plan to transition your practice
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 pt-2">
          {/* Year selector */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">Year</label>
            <div className="grid grid-cols-5 gap-1.5 max-h-[160px] overflow-y-auto rounded-md border border-border p-2">
              {years.map((y) => {
                const disabled = isYearFullyDisabled(y);
                return (
                  <button
                    key={y}
                    disabled={disabled}
                    onClick={() => {
                      setYear(y);
                      // If currently selected month is disabled for new year, clear it
                      if (month !== null && isMonthDisabled(y, month)) {
                        setMonth(null);
                      }
                    }}
                    className={cn(
                      "rounded-md px-2 py-1.5 text-sm font-medium transition-colors",
                      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                      disabled && "text-muted-foreground/40 cursor-not-allowed",
                      !disabled && year === y
                        ? "bg-primary text-primary-foreground shadow-sm"
                        : !disabled
                          ? "hover:bg-accent hover:text-accent-foreground text-foreground"
                          : ""
                    )}
                  >
                    {y}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Month selector */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">Month</label>
            <div className="grid grid-cols-4 gap-1.5">
              {MONTHS.map((m, i) => {
                const disabled = year === null || isMonthDisabled(year, i);
                return (
                  <button
                    key={m}
                    disabled={disabled}
                    onClick={() => setMonth(i)}
                    className={cn(
                      "rounded-md px-3 py-2 text-sm font-medium transition-colors",
                      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                      disabled && "text-muted-foreground/40 cursor-not-allowed",
                      !disabled && month === i
                        ? "bg-primary text-primary-foreground shadow-sm"
                        : !disabled
                          ? "hover:bg-accent hover:text-accent-foreground text-foreground"
                          : ""
                    )}
                  >
                    {m}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Preview */}
          {year !== null && month !== null && (
            <div className="text-center text-sm text-muted-foreground">
              Selected: <span className="font-semibold text-foreground">{MONTHS[month]} {year}</span>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onOpenChange(false)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              disabled={!canConfirm}
              onClick={() => {
                if (year !== null && month !== null) {
                  onConfirm(year, month);
                }
              }}
            >
              {saving ? "Saving…" : "Confirm Date"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Main TimelineCard ──────────────────────────────────────────────

export function TimelineCard() {
  const { user } = useAuth();
  const { effectiveTeamId } = useTeamId();
  const { teamData, profileData, loading: companyLoading } = useCompanyData();
  const queryClient = useQueryClient();

  const [a2TimelineLabel, setA2TimelineLabel] = useState<string | null>(null);
  const [a2Loading, setA2Loading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [localSuccessionDate, setLocalSuccessionDate] = useState<Date | null>(null);

  // Sync local state from teamData on mount/refresh
  useEffect(() => {
    if (teamData?.succession_target_date && !localSuccessionDate) {
      setLocalSuccessionDate(new Date(teamData.succession_target_date));
    }
  }, [teamData?.succession_target_date]);

  // Load A2 fallback timeline
  useEffect(() => {
    async function loadA2Timeline() {
      if (!user?.id) {
        setA2Loading(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from("assessment_progress")
          .select("answers")
          .eq("user_id", user.id)
          .eq("module_id", "owner-readiness-exit-timeline")
          .maybeSingle();

        if (error) {
          console.error("[TimelineCard] A2 query error:", error.message);
          setA2Loading(false);
          return;
        }

        if (data?.answers) {
          const answers = data.answers as Record<string, any>;
          const rawValue = answers?.time_to_exit;
          if (rawValue && typeof rawValue === "string" && EXIT_TIMELINE_MAP[rawValue]) {
            setA2TimelineLabel(EXIT_TIMELINE_MAP[rawValue]);
          }
        }
      } catch (err) {
        console.error("[TimelineCard] Error:", err);
      } finally {
        setA2Loading(false);
      }
    }

    loadA2Timeline();
  }, [user?.id]);

  const successionTargetDate = localSuccessionDate
    ?? (teamData?.succession_target_date
      ? new Date(teamData.succession_target_date)
      : null);

  const loading = companyLoading || a2Loading;

  // Pre-fill values for edit mode
  const prefillYear = successionTargetDate ? successionTargetDate.getFullYear() : null;
  const prefillMonth = successionTargetDate ? successionTargetDate.getMonth() : null;

  const handleConfirm = useCallback(
    async (year: number, monthIndex: number) => {
      if (!user?.id || !effectiveTeamId) return;

      const selectedDate = new Date(year, monthIndex, 1);
      setSaving(true);
      try {
        const dateStr = format(selectedDate, "yyyy-MM-dd");

        // Operation 1: Upsert date to teams table via RPC
        const teamName = teamData?.name
          || (profileData?.company_name ? profileData.company_name : "My Practice");
        const { error: teamError } = await supabase
          .rpc("upsert_team_succession_date", {
            p_team_id: effectiveTeamId,
            p_succession_date: dateStr,
            p_team_name: teamName,
          });

        if (teamError) throw teamError;

        // Operation 2: Write synthetic answer to user_assessment_responses
        const { error: responseError } = await supabase
          .from("user_assessment_responses")
          .upsert(
            {
              user_id: user.id,
              assessment_id: A2_ASSESSMENT_ID,
              question_id: SUCCESSION_QUESTION_ID,
              response_value: "specific_date" satisfies Json,
              team_id: effectiveTeamId,
            },
            { onConflict: "user_id,question_id" }
          );

        if (responseError) throw responseError;

        // Operation 3: Trigger score recalculation
        await calculateScores(user.id, effectiveTeamId, A2_ASSESSMENT_ID);

        // Optimistic local update for instant UI transition
        setLocalSuccessionDate(selectedDate);

        // Invalidate caches to refresh tile display
        queryClient.invalidateQueries({ queryKey: ["company-data", effectiveTeamId] });
        queryClient.invalidateQueries({ queryKey: ["team-id"] });

        setDialogOpen(false);
        toast({
          title: "Succession date saved",
          description: `Target date set to ${format(selectedDate, "MMMM yyyy")}`,
        });
      } catch (err: any) {
        console.error("[TimelineCard] Save error:", err);
        toast({
          title: "Failed to save succession date",
          description: err.message || "Please try again.",
          variant: "destructive",
        });
      } finally {
        setSaving(false);
      }
    },
    [user?.id, effectiveTeamId, teamData?.name, profileData?.company_name, queryClient]
  );

  const cardClassName =
    "md:col-span-3 h-full order-2 md:order-1 bg-gradient-to-r from-primary/5 to-accent/5 border-primary/20";

  const header = (
    <CardHeader className="pb-3">
      <div className="flex items-center gap-2">
        <Calendar className="h-5 w-5 text-primary" />
        <CardTitle className="text-xl">Succession Timeline</CardTitle>
      </div>
    </CardHeader>
  );

  // Loading state
  if (loading) {
    return (
      <Card className={cardClassName}>
        {header}
        <CardContent className="space-y-3">
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-32" />
        </CardContent>
      </Card>
    );
  }

  // ── Filled state ───────────────────────────────────────────────
  if (successionTargetDate) {
    const formattedDate = format(successionTargetDate, "MMMM yyyy");
    const { label: remainingLabel, isOverdue } = getTimeRemaining(successionTargetDate);

    return (
      <>
        <Card className={cardClassName}>
          {header}
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-lg text-foreground">
                  Target succession date
                </h3>
                {a2TimelineLabel && (
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Assessment estimate: {a2TimelineLabel}
                  </p>
                )}
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-foreground">{formattedDate}</div>
            <div
                  className={cn(
                    "text-sm font-medium",
                    isOverdue ? "text-warning" : "text-muted-foreground"
                  )}
                >
                  {remainingLabel}
                </div>
              </div>
            </div>

            {(() => {
              const totalYears = successionTargetDate.getFullYear() - new Date().getFullYear();
              const progress = Math.min(Math.max((30 - totalYears) / 30 * 100, 2), 100);
              return (
                <div className="flex items-center gap-3">
                  <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden">
                    <div
                      className="h-full rounded-full bg-primary transition-all duration-500"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                  <span className="text-xs text-muted-foreground whitespace-nowrap">
                    {Math.round(progress)}% complete
                  </span>
                </div>
              );
            })()}

            <button
              onClick={() => setDialogOpen(true)}
              className="text-xs text-primary hover:underline inline-flex items-center gap-1"
            >
              <Pencil className="h-3 w-3" />
              Edit date
            </button>
          </CardContent>
        </Card>

        <YearMonthPicker
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          selectedYear={prefillYear}
          selectedMonth={prefillMonth}
          onConfirm={handleConfirm}
          saving={saving}
        />
      </>
    );
  }

  // ── Empty state ────────────────────────────────────────────────
  return (
    <>
      <Card className={cardClassName}>
        {header}
        <CardContent>
          <div className="flex flex-col items-center justify-center text-center py-4 space-y-3">
            <div className="rounded-full bg-primary/10 p-3">
              <Clock className="h-6 w-6 text-primary" />
            </div>
            <div className="space-y-1">
              <h3 className="font-semibold text-foreground">No succession date selected</h3>
              <p className="text-sm text-muted-foreground max-w-xs">
                Set a target date to track your timeline progress
              </p>
            </div>
            <Button onClick={() => setDialogOpen(true)} size="sm">
              Select Your Succession Date
            </Button>
          </div>
        </CardContent>
      </Card>

      <YearMonthPicker
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        selectedYear={null}
        selectedMonth={null}
        onConfirm={handleConfirm}
        saving={saving}
      />
    </>
  );
}
